/**
 * Convert GraphQL-style operators to BFF REST query params.
 *
 * BFF contract:
 * - Pagination: page, limit, sort_by, sort_order
 * - Filters: flat query params per endpoint (status=confirmed, isRemoved=false)
 * - Ranges: fieldFrom / fieldTo (e.g. dateFrom, dateTo, priceFrom, priceTo)
 * - Search: field name with value (BFF handles ILIKE internally)
 * - Arrays: comma-separated values (warehouseId=id1,id2)
 *
 * We skip "whereAggregate" operators — the BFF counts internally.
 */
/**
 * Tope de items que acepta el BFF en un pedido. Es el `limit` que se manda
 * cuando el caller pide la lista completa (`hasPagination: false`); pedir más no
 * trae más.
 */
const FULL_LIST_LIMIT = 10000;

/**
 * La base de la query key de una consulta paginada: el `queryKey`, el negocio,
 * el `uniqueQuery` del caller **y los parámetros que viajan en el pedido**.
 *
 * Antes la key no llevaba los `operators` ni los `extraParams`, y cada caller
 * tenía que acordarse de repetirlos en `uniqueQuery`. Cuando no lo hacía, dos
 * pantallas que piden el mismo endpoint con filtros distintos compartían caché
 * durante 60 segundos: la configuración del Big Supplier (sin inactivos) y
 * Productos publicados (con inactivos) se mostraban la lista de la otra
 * (trd-client#297).
 *
 * Los parámetros van al final de la base, así la invalidación por prefijo
 * (`[querykeys.x]`) sigue alcanzando a todas. La paginación se agrega después.
 */
export function paginatedQueryKey({
  queryKey,
  businessId,
  uniqueQuery = [],
  operators = [],
  extraParams = {},
}: {
  queryKey: string;
  businessId?: string;
  uniqueQuery?: any[];
  operators?: any[];
  extraParams?: Record<string, any>;
}): any[] {
  return [
    queryKey,
    businessId,
    ...uniqueQuery,
    JSON.stringify({ operators, extraParams }),
  ];
}

export function buildRestParams(
  operators: any[],
  hasPagination: boolean,
  page: number,
  limit: number
): Record<string, any> {
  const params: Record<string, any> = {};

  if (hasPagination) {
    params.page = page;
    params.limit = limit;
  } else {
    // `hasPagination: false` significa "traeme la lista completa", y hasta acá
    // se traducía a NO mandar `limit`. Pero el BFF tiene default 20
    // (`PaginationParams::limit`), así que el caller que pedía todo recibía la
    // primera página y no se enteraba: creía tener la lista entera.
    //
    // Eso es lo que rompía la pantalla de Deudores, que además filtraba y
    // SUMABA sobre lo recibido (trudapp/trd-bff#159). Los hooks que ya sabían
    // del problema lo parcheaban con `extraParams: { limit: 10000 }` cada uno
    // por su cuenta; el default vive acá, así que ningún caller nuevo vuelve a
    // caer en la trampa.
    // …salvo que el caller haya pedido un tamaño explícito con el operador
    // `limit`: pedir "existe alguno" (limit 1) y recibir 10.000 filas es la otra
    // mitad de la misma trampa. El operador se saltea más abajo, así que sin
    // esto su valor se perdía en silencio.
    const explicitLimit = operators.find(
      (op: any) => op?.operator === "limit" && op?.variableValue != null
    )?.variableValue;
    params.limit = explicitLimit ?? FULL_LIST_LIMIT;
  }

  for (const op of operators) {
    // Skip pagination operators (handled above)
    if (op.operator === "limit" || op.operator === "offset") continue;

    // Skip aggregate operators — BFF handles count internally
    if (
      op.operator === "whereAggregate" ||
      op.operator === "customWhereAggregate"
    )
      continue;

    // Order by → sort_by / sort_order
    if (op.operator === "order_by" && op.fieldName) {
      params.sort_by = op.fieldName;
      params.sort_order = (
        op.value ??
        op.variableValue ??
        "desc"
      ).toUpperCase();
      continue;
    }

    // Where filters
    if (
      (op.operator === "where" ||
        op.operator === "customWhere" ||
        op.operator === "_or") &&
      op.fieldName &&
      op.variableValue !== undefined
    ) {
      const field = op.fieldName;
      const value = op.variableValue;
      const operation = op.operation;

      // Equality: _eq → flat param
      if (operation === "_eq") {
        params[field] = value;
        continue;
      }

      // Not equal: _neq -> `${field}Not` (param de exclusión del BFF; lo ignora
      // hasta soportarlo, ver issue). Antes caía al fallback y se mandaba como
      // `_eq` (p.ej. status=cancelled), INVIRTIENDO el filtro de asignar-cobro.
      if (operation === "_neq") {
        params[`${field}Not`] = value;
        continue;
      }

      // Greater than: _gt, _gte → fieldFrom
      if (operation === "_gt" || operation === "_gte") {
        // Date fields use dateFrom/dateTo convention
        if (field === "createdAt" || field === "startReservation") {
          params.dateFrom = value;
        } else {
          params[`${field}From`] = value;
        }
        continue;
      }

      // Less than: _lt, _lte → fieldTo
      if (operation === "_lt" || operation === "_lte") {
        if (field === "createdAt" || field === "startReservation") {
          params.dateTo = value;
        } else {
          params[`${field}To`] = value;
        }
        continue;
      }

      // Array membership: _in → comma-separated
      if (operation === "_in") {
        params[field] = Array.isArray(value) ? value.join(",") : value;
        continue;
      }

      // Search/like: _ilike → strip % wildcards.
      //
      // Por defecto viaja como `search`, que es la convención multi-campo del
      // BFF (Criteria). Pero NO todos los endpoints la hablan: los cinco de
      // pricing (`price-lists`, `price-plans`, `price-wholesaler-plans`,
      // `price-templates`, `checkout-fees`) reciben el término en `name` y
      // hacen el ILIKE ellos. Mandarles `search` es tirar el filtro a la
      // basura: serde descarta el campo desconocido, el endpoint devuelve la
      // lista entera y el buscador de la pantalla no filtra NADA.
      //
      // `paramName` es el escape hatch para esos casos: el operator dice con
      // qué nombre viaja el término.
      if (operation === "_ilike") {
        const cleanValue =
          typeof value === "string" ? value.replace(/%/g, "").trim() : value;
        params[op.paramName || "search"] = cleanValue;
        continue;
      }

      // customWhere with an inline ILIKE (e.g. `street: {_ilike: "%foo%"}`)
      // -> collapse to the BFF Criteria multi-field `search` param (Option A).
      if (op.operator === "customWhere") {
        const ilikeMatch = String(value).match(/_ilike:\s*"%?([^"%]+)%?"/);
        if (ilikeMatch) {
          params.search = ilikeMatch[1].trim();
          continue;
        }
        // customWhere con `_in` inline (e.g. `kind: {_in: ["ORGANIZATION"]}`)
        // -> param plano comma-separated (contrato de arrays del BFF). Sin
        // esto el predicado se descartaba silenciosamente: el buyer-picker
        // con allowedKinds=["ORGANIZATION"] mostraba PERSON y dejaba elegir
        // un insurer invalido -> POST 422 BUSINESS_RULE_VIOLATION.
        const inMatch = String(value).match(/_in:\s*\[([^\]]*)\]/);
        if (inMatch) {
          const list = inMatch[1]
            .split(",")
            .map((s) => s.trim().replace(/^["']|["']$/g, ""))
            .filter(Boolean);
          if (list.length) params[field] = list.join(",");
          continue;
        }
        // customWhere with inline range (e.g. `_gte: "100", _lte: "500"`)
        const gteMatch = String(value).match(/_gte:\s*"([^"]+)"/);
        const lteMatch = String(value).match(/_lte:\s*"([^"]+)"/);
        if (gteMatch) params[`${field}From`] = gteMatch[1];
        if (lteMatch) params[`${field}To`] = lteMatch[1];
        continue;
      }

      // Fallback: send as-is
      params[field] = value;
    }
  }

  return params;
}

/**
 * Claves que `buildRestParams` agrega por la paginación/orden y que NO son un
 * filtro. El `POST /export` lleva su propio `limit` y su propio `orderBy` (los
 * elige el usuario en el diálogo), así que mandarlas dentro de `source.params`
 * sería, en el mejor caso, ruido.
 */
const PAGINATION_PARAM_KEYS = ["page", "limit", "sort_by", "sort_order"];

/**
 * Filtros vigentes de una pantalla → `source.params` del `POST /export`.
 *
 * POR QUÉ EXISTE: el export server-side vuelve a correr el read de lista del
 * módulo en el BFF, y le pasa `source.params` como si fueran los query params
 * del GET de esa pantalla (ver `trd-bff/src/modules/export/application/registry.rs`).
 * Si esos params no son los mismos que manda la lista, el archivo sale con
 * OTROS filtros que los que el usuario está viendo —y con un nombre que promete
 * los de la pantalla—. Filtrar por un mes y bajarse la tabla entera es peor que
 * el botón roto.
 *
 * Por eso no se arma a mano: se reusan los MISMOS operadores que la pantalla le
 * pasa a su hook REST y la misma traducción (`buildRestParams`). Así los dos
 * pedidos no pueden divergir sin que alguien cambie el hook.
 *
 * Lo único que se saca es la paginación, y todo se manda como texto: del otro
 * lado `params` es un `HashMap<String, String>` y un número crudo no
 * deserializa.
 */
export function buildExportParams(
  operators: any[] = []
): Record<string, string> {
  const raw = buildRestParams(operators, false, 1, 0);
  const params: Record<string, string> = {};

  for (const [key, value] of Object.entries(raw)) {
    if (PAGINATION_PARAM_KEYS.includes(key)) continue;
    if (value === undefined || value === null) continue;

    const text = Array.isArray(value) ? value.join(",") : String(value);
    if (text === "") continue;

    params[key] = text;
  }

  return params;
}
